import { type Dispatch, type SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FilterOptionsLoader from '@/components/shared/loader/filter-options-loader';
import CheckboxWithLabel from '@/components/ui/checkbox-with-label';
import useGetJourneyListData from '@/hooks/use-get-journey-list-data';
import { DEFAULT_RESPONSE_LIMIT } from '@/query/constants';
import { getToday } from '@/utils/helpers/get-formatted-date-for-api';

interface RouteAccordionContentProps {
  selectedRoutes: { journeyId: string; lineNumber: string }[];
  setSelectedRoutes: Dispatch<SetStateAction<{ journeyId: string; lineNumber: string }[]>>;
}

const RouteAccordionContent = ({
  selectedRoutes,
  setSelectedRoutes,
}: RouteAccordionContentProps) => {
  const { t } = useTranslation();
  const today = useMemo(() => getToday(), []);

  const { data: journeyData, isLoading: isJourneyLoading } = useGetJourneyListData({
    date: today,
    status: 'active',
    sort: 'line_number',
    limit: DEFAULT_RESPONSE_LIMIT,
  });

  const validJourneys = journeyData || [];

  const handleJourneysChange = (journeyId: string, lineNumber: string) => {
    setSelectedRoutes((prevJourneys) => {
      const isSelected = prevJourneys.some((route) => route.journeyId === journeyId);

      if (isSelected) {
        return prevJourneys.filter((route) => route.journeyId !== journeyId);
      }

      return [...prevJourneys, { journeyId, lineNumber }];
    });
  };

  if (isJourneyLoading) {
    return <FilterOptionsLoader />;
  }

  if (validJourneys.length === 0) {
    return <div className="p-2 text-sm text-muted-foreground">{t('errors.routes.empty')}</div>;
  }

  return (
    <div className="flex flex-col text-balance">
      {validJourneys.map(({ lineNumber, id: journeyId, journeyNumber }) => (
        <div
          className="flex items-center px-1 py-2 hover:bg-accent rounded"
          key={`incident-filter-${journeyId}`}
        >
          <CheckboxWithLabel
            id={journeyId}
            checked={selectedRoutes.some((route) => route.journeyId === journeyId)}
            className="data-[state=checked]:border-primary data-[state=checked]:bg-primary"
            onCheckedChange={() => handleJourneysChange(journeyId, lineNumber)}
            text={`${lineNumber} ${journeyNumber}`}
          />
        </div>
      ))}
    </div>
  );
};

export default RouteAccordionContent;
